/**
 * Felix Countdown — <felix-countdown target="<unix seconds>">
 *
 * Markup comes from felix_get_countdown() (inc/components/countdown.php): one
 * span per unit, marked data-unit="d|h|m|s" (plus data-unpadded when it drops
 * the leading zero), largest first.
 *
 * Rewrites the numbers inside the server-rendered digit spans; it never touches
 * the markup, so the line can't reflow.
 *
 * The digits are correct for the moment the page was generated, which on a
 * cached page can be hours ago — hence the first tick runs immediately rather
 * than after a second. At zero it freezes at 00, unless the markup carries a
 * <template data-countdown-ended>: then that content replaces the nearest
 * [data-countdown-scope] (or the countdown itself). Either way the server
 * stops rendering it on the next request.
 *
 * @package Felix
 */

const pad = (value) => String(value).padStart(2, '0')

const SIZES = { d: 86400, h: 3600, m: 60, s: 1 }

class FelixCountdown extends HTMLElement {
	connectedCallback() {
		this.target = parseInt(this.getAttribute('target'), 10)
		this.units = [...this.querySelectorAll('[data-unit]')].filter((unit) => SIZES[unit.dataset.unit])

		if (!this.target || !this.units.length) return

		if (this.tick() > 0) this.timer = setInterval(() => this.tick(), 1000)
	}

	disconnectedCallback() {
		clearInterval(this.timer)
	}

	tick() {
		const remaining = Math.max(0, this.target - Math.floor(Date.now() / 1000))
		let left = remaining

		// Whichever unit leads carries the whole remainder, so without a days unit a two-day countdown reads 48H.
		this.units.forEach((unit) => {
			const size = SIZES[unit.dataset.unit]
			const value = Math.floor(left / size)
			unit.textContent = unit.hasAttribute('data-unpadded') ? String(value) : pad(value)
			left %= size
		})

		if (remaining === 0) {
			clearInterval(this.timer)
			this.end()
		}

		return remaining
	}

	end() {
		const ended = this.querySelector('template[data-countdown-ended]')
		if (!ended) return
		;(this.closest('[data-countdown-scope]') || this).replaceWith(ended.content.cloneNode(true))
	}
}

if (!customElements.get('felix-countdown')) {
	customElements.define('felix-countdown', FelixCountdown)
}
